import { ApolloError } from "@apollo/client";
import { useDiscountValidatePromocodeMutation } from "gql/generated";
import i18n, { i18nLanguage, i18nNamespace, loadLanguagePacks } from "i18n";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormHandler } from "shared/hooks";
import {
  convertCartToProductPurchaseRequests,
  useBookerContext,
  useCartContext,
} from "widgets/booker/contexts";
import { z } from "zod";

export const getValidatePromoCodeSchema = () =>
  z.object({
    promoCode: z
      .string({
        required_error: i18n.t("booker:CHECKOUT.PROMO_CODE.PLEASE_ENTER_CODE"),
      })
      .trim()
      .min(1, {
        message: i18n.t("booker:CHECKOUT.PROMO_CODE.PLEASE_ENTER_CODE"),
      }),
  });

export type ValidatePromoCodeFormValues = z.infer<
  ReturnType<typeof getValidatePromoCodeSchema>
>;

/**
 * Substring of the backend's `PromoCodeNotApplicableError` message, used to
 * distinguish a tier-scope mismatch from a generic invalid/expired/depleted
 * code so the form can show a more specific error.
 */
const PROMO_CODE_NOT_APPLICABLE_MESSAGE =
  "Promo code does not apply to the selected option";

type UseValidatePromoCodeFormProps = {
  onSuccess: () => void;
  onError: (isNotApplicable: boolean) => void;
};

export const useValidatePromoCodeForm = ({
  onSuccess,
  onError,
}: UseValidatePromoCodeFormProps) => {
  const { promoCode, updatePromoCode, cart, selectedTierId } =
    useCartContext();
  const { booker, product } = useBookerContext();

  const [validatePromoCode] = useDiscountValidatePromocodeMutation();

  const { i18n } = useTranslation();
  /**
   * Instead of using a const schema, we need to use a state variable
   * because the schema is dynamically updated when the language changes
   * - This will load the "en" schema initially, and then if the language
   *   changes, will load the schema of the new language
   */
  const [schema, setSchema] = useState<
    ReturnType<typeof getValidatePromoCodeSchema>
  >(getValidatePromoCodeSchema());

  /**
   * Dynamically update the schema when the language changes
   * - When the language is changed, we need to reload the schema to update the
   *   error messages to the new language
   */
  useEffect(() => {
    const loadLanguagePacksAndUpdateSchemas = async () => {
      await loadLanguagePacks(
        [i18nNamespace.Core, i18nNamespace.Booker],
        "booker",
        i18n.language as i18nLanguage,
      );
      setSchema(getValidatePromoCodeSchema());
    };
    loadLanguagePacksAndUpdateSchemas();
  }, [i18n.language, setSchema]);

  return useFormHandler<ValidatePromoCodeFormValues>(
    useCallback(
      async data => {
        if (data && data.promoCode) {
          await validatePromoCode({
            variables: {
              promoCode: data.promoCode,
              storefrontId: booker?.storefront,
              bookerId: booker?._id,
              productPurchaseRequests: convertCartToProductPurchaseRequests(
                cart,
                product?.pricing,
                selectedTierId,
              ),
            },
          })
            .then(response => {
              if (response.data && response.data.discountValidatePromocode) {
                updatePromoCode(
                  response.data.discountValidatePromocode.promoCode ?? "",
                );
                onSuccess();
              } else onError(false);
            })
            .catch(err => {
              const message = err instanceof ApolloError ? err.message : "";
              onError(message.includes(PROMO_CODE_NOT_APPLICABLE_MESSAGE));
            });
        }
      },
      [
        booker?._id,
        booker?.storefront,
        cart,
        onError,
        onSuccess,
        product?.pricing,
        selectedTierId,
        updatePromoCode,
        validatePromoCode,
      ],
    ),
    { promoCode },
    { schema },
  );
};
